import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import { useLocale } from 'next-intl'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  fetchSupportPayload,
  useSupportTarget,
} from '@/services/support/support.client'
import {
  supportQueryKey,
  type SupportTarget,
} from '@/services/support/supportContract'
import {
  getProjectQuery,
  listProjectsAndMembersQuery,
  projectsByOrganizationQuery,
} from '@/services/graphql'
import { resolveProjectPermissions } from '@/features/project/project.permissions'
import { projectAdapter, projectCardAdapter } from '@/services/adapters'
import { useUserContext } from '@/context/UserContext'
import { Language, ProjectResponse, ProjectSchema } from '@/types'
import { referenceQueryPreset } from '@/services/query-presets'

const client = getAmplifyClient()
const PROJECT_MEMBERSHIP_LOOKUP_CONCURRENCY = 8
const ACCEPTED_MEMBER_STATUS = 'DONE'
const PROJECT_MEMBER_TYPE = 'PROJECT'

type ProjectMembershipItem = {
  projectID?: string | null
  status?: string | null
  type?: string | null
}

type ProjectsByOrganizationResponse = {
  projectsByOrganization?: {
    items?: ProjectSchema[] | null
  } | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isProjectMembershipItem = (
  value: unknown,
): value is ProjectMembershipItem => {
  if (!isRecord(value)) return false
  if (!('projectID' in value)) return true
  if (typeof value.projectID !== 'string' && value.projectID !== null) {
    return false
  }
  if (
    'status' in value &&
    typeof value.status !== 'string' &&
    value.status !== null
  ) {
    return false
  }
  if (
    'type' in value &&
    typeof value.type !== 'string' &&
    value.type !== null
  ) {
    return false
  }
  return true
}

const isAcceptedProjectMembership = (
  item: ProjectMembershipItem,
): item is ProjectMembershipItem & { projectID: string } =>
  typeof item.projectID === 'string' &&
  item.projectID.length > 0 &&
  item.status === ACCEPTED_MEMBER_STATUS &&
  item.type === PROJECT_MEMBER_TYPE

const getProjectMembershipItems = (data: unknown): ProjectMembershipItem[] => {
  if (!isRecord(data)) {
    throw new Error('Invalid project membership response')
  }

  const connection = data.organizationMemberbyUser
  if (!isRecord(connection)) {
    throw new Error('Invalid project membership response')
  }

  const items = connection.items
  if (!Array.isArray(items)) {
    throw new Error('Invalid project membership response')
  }

  return items.filter(isProjectMembershipItem)
}

const mapWithConcurrency = async <Input, Output>(
  items: Input[],
  concurrency: number,
  mapper: (item: Input, index: number) => Promise<Output>,
): Promise<Output[]> => {
  if (items.length === 0) return []

  const results = new Array<Output>(items.length)
  let nextIndex = 0
  const workerCount = Math.min(Math.max(concurrency, 1), items.length)

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex
        nextIndex += 1
        results[currentIndex] = await mapper(items[currentIndex], currentIndex)
      }
    }),
  )

  return results
}

export const useProject = (id: string | undefined) => {
  const { ownership, authorization, organizationScope } = useUserContext()
  const supportTarget = useSupportTarget()

  return useQuery({
    queryKey: supportQueryKey(
      ['project', id],
      supportTarget,
      organizationScope.activeOrganizationId,
    ),
    queryFn: async ({ signal }) =>
      await getProject(
        id!,
        supportTarget,
        organizationScope.activeOrganizationId,
        signal,
      ),
    staleTime: referenceQueryPreset.staleTime,
    enabled: !!id && id !== 'create',
    select: (data) => {
      if (!data) {
        return null
      }

      const projectPermissions = resolveProjectPermissions({
        authorization,
        ownership,
        activeOrganizationId: organizationScope.activeOrganizationId,
        project: data,
      })

      return {
        ...data,
        isGuest:
          projectPermissions.isGuestAccount ||
          projectPermissions.isExternalProject,
      }
    },
  })
}

export const getProject = async (
  id: string,
  supportTarget?: SupportTarget | null,
  organizationID?: string,
  signal?: AbortSignal,
) => {
  if (supportTarget) {
    const project = await getSupportProjectSchema(
      id,
      supportTarget,
      organizationID,
      signal,
    )
    return project ? projectAdapter(project) : null
  }
  try {
    const { data } = (await client.graphql({
      query: getProjectQuery,
      variables: { id },
    })) as GraphQLResult<ProjectResponse>
    return data?.getProject ? projectAdapter(data.getProject) : null
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getSupportProjectSchema = async (
  id: string,
  supportTarget: SupportTarget,
  organizationID?: string,
  signal?: AbortSignal,
) => {
  if (!organizationID) throw new Error('Support organization scope is required')
  const project = await fetchSupportPayload<ProjectSchema | null>(
    `query ConsoleSupportProject($targetUserID: ID!, $organizationID: ID!, $id: ID!) {
    consoleSupportProject(targetUserID: $targetUserID, organizationID: $organizationID, id: $id) { payload }
  }`,
    'consoleSupportProject',
    { targetUserID: supportTarget.targetUserID, organizationID, id },
    signal,
  )
  if (
    project &&
    (project.id !== id || project.organizationID !== organizationID)
  )
    throw new Error('Support project scope could not be confirmed')
  return project
}

const getProjectSchema = async (id: string) => {
  const { data } = (await client.graphql({
    query: getProjectQuery,
    variables: { id },
  })) as GraphQLResult<ProjectResponse>
  return data?.getProject ?? null
}

const mergeProjects = (
  organizationProjects: ProjectSchema[],
  userProjects: ProjectSchema[],
) => {
  const mergedProjects = [...organizationProjects, ...userProjects]
  return uniqBy(mergedProjects, 'id')
}

export const loadProjectsForUser = async ({
  userID,
  organizationID,
  supportTarget,
}: {
  userID: string | undefined
  organizationID: string | undefined | null
  supportTarget?: SupportTarget | null
}) => {
  const organizationProjects = organizationID
    ? await listProjects(organizationID)
    : []

  try {
    const userProjects = await listProjectsAndMembers(
      userID,
      supportTarget,
      organizationID ?? undefined,
    )
    return mergeProjects(organizationProjects, userProjects)
  } catch (error) {
    if (!organizationID) throw error

    console.warn('Unable to load project memberships; using team projects', {
      error,
      userID,
      organizationID,
    })
    return organizationProjects
  }
}

export const useProjects = () => {
  const locale = useLocale()
  const lang = locale.toLocaleUpperCase() as Language

  const { ownership, isGuest, organizationScope } = useUserContext()
  const supportTarget = useSupportTarget()

  const userID = ownership?.byUserID
  const organizationID = organizationScope.activeOrganizationId

  return useQuery({
    queryKey: supportQueryKey(
      ['projects', { userID, organizationID }],
      supportTarget,
    ),
    queryFn: async () =>
      loadProjectsForUser({
        userID,
        organizationID,
        supportTarget,
      }),
    ...referenceQueryPreset,
    enabled: !!userID,
    placeholderData: [],
    select: (data) =>
      data.map((project) => projectCardAdapter(project, lang, isGuest)),
  })
}

const listProjectsAndMembers = async (
  userID: string | undefined,
  supportTarget?: SupportTarget | null,
  organizationID?: string,
) => {
  if (!userID) return []

  const membershipResult = (await client.graphql({
    query: listProjectsAndMembersQuery,
    variables: {
      userID,
      status: { eq: ACCEPTED_MEMBER_STATUS },
      filter: { type: { eq: PROJECT_MEMBER_TYPE } },
    },
  })) as { data?: unknown }
  const items = getProjectMembershipItems(membershipResult.data)
  const projectIds = [
    ...new Set(
      items.filter(isAcceptedProjectMembership).map((item) => item.projectID),
    ),
  ]

  const projects = await mapWithConcurrency(
    projectIds,
    PROJECT_MEMBERSHIP_LOOKUP_CONCURRENCY,
    (id) =>
      supportTarget
        ? getSupportProjectSchema(id, supportTarget, organizationID)
        : getProjectSchema(id),
  )

  return projects.filter((project): project is ProjectSchema =>
    Boolean(project),
  )
}

const listProjects = async (organizationID: string) => {
  try {
    const { data } = (await client.graphql({
      query: projectsByOrganizationQuery,
      variables: {
        organizationID,
        limit: 1000,
      },
    })) as GraphQLResult<ProjectsByOrganizationResponse>

    return data?.projectsByOrganization?.items ?? []
  } catch (e) {
    console.error(e)
    throw e
  }
}
